import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useActiveProfile, isActiveProfileValid } from '@/store/localStorage/useTokenState';
import { selectActiveProfile } from '@/store/user/selectors/selectActiveProfile';
import type { RootState } from '@/app/store';
import { useGetDiscussionsQuery, DISCUSSIONS_PAGE_SIZE } from './discussionsApi';
import type { DiscussionsMeta } from './discussionsApi';
import type { DiscussionEntry, DiscussionNotificationType, FilterMode } from './types';

const FILTER_MODE_TO_NOTIFICATION_TYPE: Record<FilterMode, DiscussionNotificationType> = {
  ownBlog: 'comments',
  otherBlogs: 'discussions',
  mentions: 'mentions'
};

export function useDiscussions(filterMode: FilterMode) {
  const [searchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page') || '1');

  const { activeProfileId } = useActiveProfile();
  const myBlogSlug: string =
    useSelector((state: RootState) => selectActiveProfile(state, { activeProfileId }))?.blogSlug ?? '';

  const { data, isFetching, isError } = useGetDiscussionsQuery(
    {
      blogSlug: myBlogSlug,
      notificationType: FILTER_MODE_TO_NOTIFICATION_TYPE[filterMode],
      page: currentPage,
      pageSize: DISCUSSIONS_PAGE_SIZE
    },
    { skip: !myBlogSlug || !isActiveProfileValid(activeProfileId) }
  );

  const discussions = data ?? [];
  const meta = (data as (DiscussionEntry[] & { meta?: DiscussionsMeta }) | undefined)?.meta;
  const totalRecords = meta?.totalEntries ?? discussions.length;
  const showPagination = totalRecords > DISCUSSIONS_PAGE_SIZE;

  return {
    discussions,
    currentPage,
    totalRecords,
    pageSize: DISCUSSIONS_PAGE_SIZE,
    showPagination,
    isFetching,
    isError
  };
}
